import { Parcel } from '@the-real-insight/tri-model';

import { authenticatedFetch } from '../JWTAuthService';
import { configuration } from '../Configuration';

/**
 * Grundstückssuche über den Flurstücksindex des Servers (`/map-layers/parcel-search`): ein Gebiet aus der Heatmap,
 * harte Kriterien, eine Sortierung. Die Treffer kommen mit Umring, damit Karte und Tabelle dieselben Zeilen zeigen.
 */

export type SearchSort = 'reserve' | 'pv' | 'area' | 'storeys' | 'brwAsc';
export type SearchBasis = 'BP' | '34' | '35' | 'unklar';
export type ConversionSignal = 'gewerbeImWohngebiet' | 'gebaeudeImWohngebiet' | 'brache' | 'untergenutzt';
// „brache“ bleibt im Typ, wird aber nicht angeboten, bis der Index den Zustand stillgelegter Flächen kennt.
export const CONVERSION_SIGNALS: ConversionSignal[] = ['gewerbeImWohngebiet', 'gebaeudeImWohngebiet', 'untergenutzt'];
const CONVERSION_LABEL: Record<ConversionSignal, string> = {
    gewerbeImWohngebiet: 'Gewerbe, Plan erlaubt Wohnen',
    gebaeudeImWohngebiet: 'Gewerbebau im Wohngebiet',
    brache: 'Brache',
    untergenutzt: 'Gewerbefläche kaum genutzt',
};

export interface ParcelSearchCriteria {
    minReserveM2?: number;
    minAreaM2?: number;
    maxAreaM2?: number;
    basis?: SearchBasis[];
    uses?: string[];
    vacant?: boolean;
    conversion?: boolean;
    conversionSignals?: ConversionSignal[];
    minAddStoreys?: number;
    minPvYieldKWh?: number;
    maxBrw?: number;
    includeUnbuildable?: boolean;
    sort?: SearchSort;
}

export interface ParcelHit {
    _id: string;
    number: string | null;
    gemarkung: string | null;
    gemeinde: string | null;
    lagebez: string | null;
    centerLat: number;
    centerLon: number;
    areaM2: number;
    landUse: string | null;
    rights: { basis: SearchBasis; planId: string | null; planName: string | null; uses: string[]; grz: number | null; gfz: number | null; z: number | null };
    existing: { buildings: number; footprintM2: number | null; floorAreaM2: number | null; grz: number | null; gfz: number | null; maxStoreys: number | null };
    reserve: { basis: string; floorAreaM2Low: number; floorAreaM2High: number } | null;
    reserveFloorAreaM2: number;
    brw: { value: number; use: string | null; stage: string | null; date: string | null } | null;
    pv: { kWp: number | null; yieldKWh: number };
    vacant: boolean;
    addStoreys: number;
    conversion?: ConversionSignal[];
    geometry: any;
}

export interface ParcelSearchResult {
    total: number;
    totalCapped: boolean;
    skip: number;
    limit: number;
    rows: ParcelHit[];
    indexed: boolean;
    indexedAt: string | null;
}

export const SEARCH_PAGE = 100;

export const searchParcels = async (geometry: any, criteria: ParcelSearchCriteria, skip = 0): Promise<ParcelSearchResult> => {
    const response = await authenticatedFetch(`${configuration.serverUrl}/map-layers/parcel-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ geometry, ...criteria, limit: SEARCH_PAGE, skip }),
    });
    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(detail?.error ?? `Die Suche ist fehlgeschlagen (${response?.status ?? 'keine Antwort'})`);
    }
    return response.json();
};

const format = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? new Intl.NumberFormat('de-DE').format(Math.round(value)) : '–';

export const BASIS_LABEL: Record<SearchBasis, string> = { BP: 'Bebauungsplan', '34': '§ 34', '35': '§ 35', unklar: 'unklar' };

/** Was am Treffer zählt, kurz: Baulücke oder Aufstockung, dazu die Hinweise auf Umnutzung. */
export const potentialOf = (hit: ParcelHit) =>
    [
        hit.vacant ? 'Baulücke' : hit.addStoreys > 0 ? `+${hit.addStoreys} Geschoss${hit.addStoreys > 1 ? 'e' : ''}` : null,
        ...(hit.conversion ?? []).map((signal) => CONVERSION_LABEL[signal] ?? signal),
    ]
        .filter(Boolean)
        .join(', ');

/** Die Reserve färbt den Treffer: je mehr Geschossfläche frei, desto kräftiger. */
const RESERVE_STEPS: [number, string][] = [
    [2000, '#5A3FB5'],
    [1000, '#7B61D1'],
    [400, '#A08BE6'],
    [0, '#C9BDF3'],
];
const reserveColor = (m2: number) => RESERVE_STEPS.find(([min]) => m2 >= min)?.[1] ?? '#C9BDF3';

export const hitElement = (hit: ParcelHit, selected: boolean): any | null => {
    if (!hit.geometry) return null;
    const lines = [
        [hit.number ? `Flurstück ${hit.number}` : null, hit.gemarkung, hit.lagebez].filter(Boolean).join(' · '),
        `Reserve ${format(hit.reserveFloorAreaM2)} m² Geschossfläche · ${format(hit.areaM2)} m² Fläche${potentialOf(hit) ? ` · ${potentialOf(hit)}` : ''}`,
    ];
    return {
        display: {
            type: 'shape',
            shape: hit.geometry,
            color: selected ? '#ff3d00' : '#3B2A80',
            fillColor: reserveColor(hit.reserveFloorAreaM2),
            fillOpacity: selected ? 0.75 : 0.55,
            weight: selected ? 3 : 1,
            opacity: 0.95,
            tooltip: lines,
        },
        // Typisiert, damit der Klick dasselbe Flurstückspanel öffnet wie die Flurstücksebene.
        entity: new Parcel({
            _id: hit._id,
            flstkennz: hit._id,
            geometry: hit.geometry,
            gemarkung: hit.gemarkung,
            lagebez: hit.lagebez,
            flaecheM2: hit.areaM2,
        } as any),
    };
};

/** Leaflet-Grenzen [[lat, lon], [lat, lon]] um einen Treffer, mit Rand. */
export const hitBounds = (hit: ParcelHit): [[number, number], [number, number]] => {
    const pad = Math.max(0.0015, Math.sqrt(hit.areaM2 || 0) / 111_000);
    return [
        [hit.centerLat - pad, hit.centerLon - pad * 1.6],
        [hit.centerLat + pad, hit.centerLon + pad * 1.6],
    ];
};

export { format as formatNumber };
